import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as PayPalLogo } from '../svg/filled/paypal.svg';
import { connect } from 'react-redux';
import { createEmbeddedPaymentSession, redirectToPayment } from '../redux/actions/checkout';
import {
    checkoutLoading,
    pageLoading,
    pagesClientSecretListSelector,
} from '../redux/selectors/pagesSelectors';
import {
    carSelectedCustomBills,
    cartMainProduct,
    cartRenewSelector
} from '../redux/selectors/cartSelector'
import { PAYMENT_PAYPAL_ENABLED } from '../constants/constants';
import { setProceedToCheckoutAfterAuth } from '../redux/actions/pagesActions';
import useTotalNumber from '../hooks/useTotalNumber';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUBLIC_KEY } from '../general/config';
import { toast } from 'react-toastify'
import { authUserData } from '../redux/selectors/authSelectors'
import StripeSection from './Payment/StripeSection'
import { ACCOUNT_TYPE } from "../redux/constants/UserConstants";

function Cards({
    redirectToPayment,
    checkoutLoading,
    cartRenew,
    setAccountFormIsVisible,
    setProceedToCheckoutAfterAuth,
    createEmbeddedPaymentSession,
    clientSecretList,
    pageLoading,
    carSelectedCustomBills,
    cartMainProduct,
    userData,
}) {
    let totalNumber = useTotalNumber();

    if (Object.keys(carSelectedCustomBills ?? {}).length > 0) {
        totalNumber = carSelectedCustomBills.cost;
    }

    const pagesClientSecret = useMemo(() => {
        if (!clientSecretList?.length || !totalNumber || !userData?.email) {
            return null;
        } else {
            const cartRenewText = cartRenew || false;
            return clientSecretList.find(
                (item) => item.key === totalNumber + cartRenewText.toString()
            )?.clientSecret;
        }
    }, [clientSecretList, totalNumber, userData?.email, cartRenew, pageLoading, cartMainProduct]);

    const [stripeLoad, setStripeLoad] = useState({ error: null });
    const isPagesClientSecretMissing = !pagesClientSecret;
    const isPersonalAccount = userData?.accountType === ACCOUNT_TYPE.PERSONAL;

    useEffect(() => {

        if (totalNumber && userData?.email) {
            if (clientSecretList && !pagesClientSecret) {
                createEmbeddedPaymentSession(totalNumber).then(res => {
                    setStripeLoad(prev => {
                        return (prev.error === res.error) ? prev : { error: res.error };
                    });
                });
            }
        }
    }, [totalNumber, clientSecretList, isPagesClientSecretMissing, userData?.email, cartRenew, createEmbeddedPaymentSession, pagesClientSecret, cartMainProduct]);

    useEffect(() => {
        if (stripeLoad.error) {
            toast.dismiss();
            toast.error("Something went wrong " + (stripeLoad?.provider ?? ''));
        }
    }, [stripeLoad])

    const [stripe, setStripe] = useState(null);

    useEffect(() => {
        if (!stripe) {
            const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);
            stripePromise.then(setStripe);
        }
    }, [stripe]);

    const handleStripe = () => {
        // TODO пересмотреть эту логику при работе над задачей https://doboard.com/1/task/23234
        if (checkoutLoading) {
            return;
        }

        if (userData?.email) {
            redirectToPayment('stripe');
        } else {
            setAccountFormIsVisible(true);
            setProceedToCheckoutAfterAuth('stripe');
        }
    };

    const handlePayPal = () => {
        if (userData?.email) {
            redirectToPayment('paypal');
        } else {
            setAccountFormIsVisible(true);
            setProceedToCheckoutAfterAuth('paypal');
        }
    };

    useEffect(() => {
        const oldFrame = document.querySelector('iframe[src*="checkout.stripe.com"]');
        if (oldFrame && pagesClientSecret) oldFrame.remove();
    }, [pagesClientSecret]);

    const checkoutTitleStripe = cartRenew
        ? 'Make a payment or subscribe to the service using a credit/debit card'
        : 'Make a one time credit/debit card payment';

    const getPaypalCardInfo = ({ isPersonalAccount, cartRenew }) => {
        if (isPersonalAccount) {
            return {
                title: (
                    <>
                        PayPal is available only for Business users. Set your account type <br/> in the{" "}
                        <a className={'link--no-underline color--blue hover-link'} href="https://cleantalk.org/my/profile#billing_info" target="_blank"
                            rel="noreferrer"
                        >
                            Billing info
                        </a>{" "}
                        section.
                    </>
                ),
                description: '',
                classDisabled: 'disabled',
            };
        }

        return {
            title: cartRenew
                ? 'Make a payment or subscribe to the service using PayPal'
                : 'Make a one-time PayPal payment',
            description: 'It may take up to 5 minutes for the payment process',
            classDisabled: '',
        };
    };

    const paypalCardInfo = useMemo(
        () => getPaypalCardInfo({ isPersonalAccount, cartRenew }),
        [isPersonalAccount, cartRenew]
    );

    return (
        <>
            <div className={'card__main'}>

                <StripeSection
                    stripe={stripe}
                    stripeLoad={stripeLoad}
                    handleStripe={handleStripe}
                    checkoutTitleStripe={checkoutTitleStripe}
                    pagesClientSecret={pagesClientSecret}
                />

                <div>
                    <div className={'card__or'}>OR</div>
                    <div className={`card__list card__list${PAYMENT_PAYPAL_ENABLED ? '' : '--single'}`}>
                        {!!PAYMENT_PAYPAL_ENABLED && (
                            <div className={'card__container'} style={{ gridColumnGap: "5px" }}>
                                <div className={paypalCardInfo.className}>
                                    <h3 className="card__title">{paypalCardInfo.title}</h3>
                                    <span className="card__description">{paypalCardInfo.description}</span>
                                </div>
                                <div className={`card__item ${paypalCardInfo.classDisabled}`}>
                                    <PayPalLogo className={'card__logo'}/>
                                </div>
                                <div className={'card__item'}>
                                    <button
                                        disabled={checkoutLoading || isPersonalAccount}
                                        onClick={() => handlePayPal()}
                                        type={'button'}
                                        className={`button card__button button--proceed ${paypalCardInfo.classDisabled}`}
                                    >
                                        <span className={'text-2 bold'}>Pay Now</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className={'notification__message mb-60 mt-0'}>
                If you are not 100% satisfied with your purchase, within 60 days from the purchase
                date, we will fully refund the cost of your order.
            </div>
        </>
    );
}

const mapStateToProps = (state) => ({
    cartMainProduct: cartMainProduct(state),
    checkoutLoading: checkoutLoading(state),
    pageLoading: pageLoading(state),
    cartRenew: cartRenewSelector(state),
    clientSecretList: pagesClientSecretListSelector(state),
    carSelectedCustomBills: carSelectedCustomBills(state),
    userData: authUserData(state),
});

const mapDispatchToProps = (dispatch) => ({
    redirectToPayment: (provider) => dispatch(redirectToPayment(provider)),
    setProceedToCheckoutAfterAuth: (provider) => dispatch(setProceedToCheckoutAfterAuth(provider)),
    createEmbeddedPaymentSession: (totalNumber) => dispatch(createEmbeddedPaymentSession(totalNumber)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Cards);
